import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

export type TemplateHelpVariable = {
  slot: string
  label: string
  source?: string
  example?: string
}

export type TemplateHelpExample = {
  title: string
  text: string
}

type TemplateVariablesHelpDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  orderNote?: string
  variablesTitle?: string
  examplesTitle?: string
  sourceLabel?: string
  examplePrefix?: string
  variables: TemplateHelpVariable[]
  examples?: TemplateHelpExample[]
  note?: string
  closeLabel?: string
}

export function TemplateVariablesHelpDialog({
  open,
  onOpenChange,
  title,
  description,
  orderNote,
  variablesTitle = 'Details you can include',
  examplesTitle = 'Ready-to-copy examples',
  sourceLabel = 'Source:',
  examplePrefix = 'Example:',
  variables,
  examples = [],
  note,
  closeLabel = 'Close',
}: TemplateVariablesHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl gap-0 overflow-hidden border-border p-0">
        <DialogHeader className="border-b border-border bg-muted/40 px-5 py-4 text-left sm:px-6">
          <DialogTitle className="pr-8 text-lg">
            {title}
          </DialogTitle>
          <p className="pr-8 text-sm text-muted-foreground">
            {description}
          </p>
          {orderNote ? (
            <p className="pr-8 text-xs text-muted-foreground">
              {orderNote}
            </p>
          ) : null}
        </DialogHeader>

        <div className="max-h-[min(72vh,640px)] space-y-4 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {variablesTitle}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {variables.map((variable) => (
                <div key={`${variable.slot}-${variable.label}`} className="flex items-start gap-2 rounded-md bg-muted/30 px-3 py-3">
                  <span className="inline-flex min-w-11 justify-center rounded-full bg-background px-2 py-1 text-xs font-medium text-foreground shadow-sm">
                    {variable.slot}
                  </span>
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium text-foreground">{variable.label}</p>
                    {variable.source ? (
                      <p className="text-xs leading-5 text-muted-foreground">
                        {sourceLabel} {variable.source}
                      </p>
                    ) : null}
                    {variable.example ? (
                      <p className="text-xs leading-5 text-muted-foreground">
                        {examplePrefix} {variable.example}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {examples.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {examplesTitle}
              </p>
              <div className="grid gap-2 lg:grid-cols-2">
                {examples.map((example) => (
                  <div key={example.title} className="rounded-md bg-muted/30 px-4 py-3 shadow-sm">
                    <p className="text-sm font-medium text-foreground">
                      {example.title}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {example.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {note ? (
            <div className="rounded-md bg-primary/5 px-4 py-3 text-sm leading-6 text-muted-foreground">
              {note}
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t border-border px-5 py-4 sm:px-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {closeLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}